import React, { createContext, useContext, useState, useCallback } from 'react';
import { cartAPI } from '../api';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [cart, setCart] = useState({ items: [], subtotal: 0, total: 0, item_count: 0 });
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const fetchCart = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await cartAPI.getCart();
      setCart(res.data.cart);
    } catch (err) {
      console.error('Failed to fetch cart', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const addToCart = async (productId, quantity = 1, source = 'catalog', deliveryDate = '') => {
    try {
      const payload = { product_id: productId, quantity, source };
      if (deliveryDate) payload.delivery_date = deliveryDate;
      await cartAPI.addToCart(payload);
      await fetchCart();
      return true;
    } catch (err) {
      throw err;
    }
  };

  const updateQuantity = async (productId, quantity) => {
    try {
      await cartAPI.updateItem(productId, { quantity });
      await fetchCart();
    } catch (err) {
      throw err;
    }
  };

  const removeItem = async (productId) => {
    try {
      await cartAPI.removeItem(productId);
      await fetchCart();
    } catch (err) {
      throw err;
    }
  };

  const applyCoupon = async (code) => {
    try {
      await cartAPI.applyCoupon(code);
      await fetchCart();
      return true;
    } catch (err) {
      throw err;
    }
  };

  const removeCoupon = async () => {
    try {
      await cartAPI.removeCoupon();
      await fetchCart();
    } catch (err) {
      throw err;
    }
  };

  return (
    <CartContext.Provider value={{ cart, loading, fetchCart, addToCart, updateQuantity, removeItem, applyCoupon, removeCoupon }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
